import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, Loader2, CalendarClock } from "lucide-react";
import { DAYS } from "@/lib/format";
import { toast } from "sonner";

const TIMES = Array.from({ length: 15 }, (_, i) => `${String(i + 7).padStart(2, "0")}:00`); // 07:00 - 21:00

export function AvailabilityManager() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState("1");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("10:00");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/availability");
      setSlots(data);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setAdding(true);
    try {
      await api.post("/availability", { day_of_week: Number(day), start_time: start, end_time: end });
      toast.success("Slot added");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/availability/${id}`); toast.success("Slot removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const grouped = DAYS.map((name, idx) => ({ name, idx, items: slots.filter((s) => s.day_of_week === idx) }));

  return (
    <div className="space-y-6">
      <Card className="p-5 rounded-2xl border-border">
        <div className="flex items-center gap-2 mb-4 text-arctic-secondary font-heading font-semibold">
          <CalendarClock className="w-5 h-5 text-arctic-primary" /> Add a weekly slot
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Day</Label>
            <Select value={day} onValueChange={setDay}>
              <SelectTrigger data-testid="avail-day"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Start</Label>
            <Select value={start} onValueChange={setStart}>
              <SelectTrigger data-testid="avail-start"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>End</Label>
            <Select value={end} onValueChange={setEnd}>
              <SelectTrigger data-testid="avail-end"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={adding} data-testid="avail-add" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-arctic-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="availability-grid">
          {grouped.map((g) => (
            <Card key={g.idx} className="p-4 rounded-2xl border-border">
              <div className="font-heading font-semibold text-arctic-secondary mb-3">{g.name}</div>
              {g.items.length === 0 ? (
                <p className="text-sm text-muted-foreground">No slots</p>
              ) : (
                <div className="space-y-2">
                  {g.items.map((s) => (
                    <div key={s.slot_id} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2 text-sm" data-testid={`slot-${s.slot_id}`}>
                      <span className="text-arctic-secondary font-medium">{s.start_time}–{s.end_time}</span>
                      <button onClick={() => remove(s.slot_id)} data-testid={`delete-slot-${s.slot_id}`} className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
